// The tier vocabulary a captured key tags its models with (arc 101 slice 02),
// and the rules the capture POST has to obey — including which models it names
// and what it is allowed to claim about each one's context window.
//
// The options are DERIVED from the domain enum, never a hardcoded string list:
// a fifth `BYOKTier` appears in the picker with no edit here, and `TIER_LABEL`
// is exhaustive BY TYPE so it cannot land without a label. All four members are
// offered on purpose — **`heavy` is the smartest one, not `smart`**:
// `JobClass.Coding` and `JobClass.Plan` both route to `heavy`
// (`packages/domain/src/value-objects/job-class-routing.ts`), so a three-choice
// picker would silently demote the two lanes that matter most.

import { BYOKTier, MAX_KEY_MODELS } from '@developerz/domain';
import type { AddKeyModelInput, ProviderModelOption } from './keys-types';

/** The ladder, cheapest → strongest — the enum's own declaration order. */
export const TIER_OPTIONS: readonly BYOKTier[] = Object.values(BYOKTier);

const TIER_LABEL: Record<BYOKTier, string> = {
  fast: 'account_keys.tier_fast',
  general: 'account_keys.tier_general',
  smart: 'account_keys.tier_smart',
  heavy: 'account_keys.tier_heavy',
};

/** Copy key for a tier. One table, so the card and the picker cannot disagree. */
export const tierLabelKey = (tier: BYOKTier): string => TIER_LABEL[tier];

/**
 * The server's ceiling on one capture's tag list — the DOMAIN's own constant, so
 * this cannot drift from the schema that refuses the 51st entry. Re-exported
 * under a local name only so the picker reads in one vocabulary; the number is
 * never restated here. The UI spends it to disable the control instead of
 * collecting 51 tags and bouncing off a 400 with the form full.
 */
export const MODEL_TAG_MAX: number = MAX_KEY_MODELS;

/**
 * Model id → the tier it was tagged with. Keyed BY MODEL ID, which is what makes
 * the duplicate the API 400s on unexpressible: there is nowhere to put a second
 * entry for one model.
 */
export type ModelTags = Readonly<Record<string, BYOKTier | undefined>>;

/** Model id → the window the provider reported for it, for the probed options. */
function windowIndex(options: readonly ProviderModelOption[]): ReadonlyMap<string, number> {
  return new Map(
    options.flatMap((o) =>
      o.context_length === undefined ? [] : [[o.id, o.context_length] as const],
    ),
  );
}

/** One body entry. The window is OMITTED, never null, when the provider gave none. */
function modelEntry(model: string, tier: BYOKTier | null, window?: number): AddKeyModelInput {
  return { model, tier, ...(window === undefined ? {} : { context_length: window }) };
}

/**
 * The TAGGED entries, sorted by id, each carrying the window its probe row
 * reported. A model the provider said nothing about carries no `context_length`
 * key at all — absent and null are different answers all the way down, and the
 * server clamps what it is given rather than inventing what it is not.
 */
export function taggedModels(
  tags: ModelTags,
  options: readonly ProviderModelOption[],
): readonly AddKeyModelInput[] {
  const windows = windowIndex(options);
  return Object.entries(tags)
    .filter((entry): entry is [string, BYOKTier] => entry[1] !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([model, tier]) => modelEntry(model, tier, windows.get(model)));
}

/**
 * The capture body's whole `models` field, or `undefined` when there is nothing
 * to say — omitting it is what asks the server for the single untagged row, and
 * `[]` is a 400 rather than a synonym for that.
 *
 * The PICKED model rides untagged when the provider reported its window, even
 * though no tier was chosen for it: it is the model every lane runs on, and
 * dropping its window is how this feature would become a dead producer for the
 * common capture where the user tags nothing. Shape is unchanged by it — the
 * domain's `captureKeyModels` treats a caller entry naming the key's own `model`
 * as THE row for that model and adds none of its own.
 *
 * Bounded by the same ceiling as the tags: 50 tags plus an untagged 51st is the
 * 400-with-the-form-full that `MODEL_TAG_MAX` exists to prevent, so at the cap
 * the picked model's window yields to the tags the reader actually chose.
 */
export function captureModels(
  model: string,
  tags: ModelTags,
  options: readonly ProviderModelOption[],
): readonly AddKeyModelInput[] | undefined {
  const tagged = taggedModels(tags, options);
  const window = windowIndex(options).get(model);
  const addPicked =
    window !== undefined && tagged.length < MODEL_TAG_MAX && !tagged.some((m) => m.model === model);
  const all = addPicked
    ? [...tagged, modelEntry(model, null, window)].sort((a, b) => a.model.localeCompare(b.model))
    : tagged;
  return all.length > 0 ? all : undefined;
}

/**
 * The models a filter box narrows to. A provider catalogue runs to ~300 entries
 * (OpenRouter), so the list is filtered, never scrolled through whole. Matching
 * is case-insensitive substring on the model ID: model ids are lower-case slugs
 * and a reader hunting `opus` should not have to know whether the vendor
 * capitalised it.
 */
export function filterModels(
  models: readonly ProviderModelOption[],
  query: string,
): readonly ProviderModelOption[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return models;
  return models.filter((m) => m.id.toLowerCase().includes(needle));
}
